import { Link } from 'react-router-dom';

/**
 * Campaign performance data
 */
export interface CampaignPerformance {
  id: number;
  name: string;
  requests_count: number;
  bot_percentage: number;
  status?: 'active' | 'paused';
}

/**
 * Props for CampaignPerformanceWidget component
 */
export interface CampaignPerformanceWidgetProps {
  /**
   * Array of top campaigns
   */
  campaigns: CampaignPerformance[];

  /**
   * Maximum campaigns to show
   */
  maxCampaigns?: number;

  /**
   * Show "View All" link
   */
  showViewAll?: boolean;

  /**
   * Loading state
   */
  isLoading?: boolean;

  /**
   * Empty state message
   */
  emptyMessage?: string;

  /**
   * Custom className
   */
  className?: string;
}

/**
 * Get performance level based on bot percentage
 */
const getPerformanceLevel = (botPercentage: number) => {
  if (botPercentage < 10) return { label: 'Excellent', color: 'bg-green-500', textColor: 'text-green-700' };
  if (botPercentage < 30) return { label: 'Good', color: 'bg-blue-500', textColor: 'text-blue-700' };
  if (botPercentage < 50) return { label: 'Moderate', color: 'bg-amber-500', textColor: 'text-amber-700' };
  return { label: 'High', color: 'bg-red-500', textColor: 'text-red-700' };
};

/**
 * CampaignPerformanceWidget Component
 *
 * Visual card-based display of campaign performance.
 * Shows key metrics with mini charts and quick actions.
 *
 * @example
 * ```tsx
 * <CampaignPerformanceWidget
 *   campaigns={topCampaigns}
 *   maxCampaigns={5}
 *   showViewAll
 * />
 * ```
 */
export function CampaignPerformanceWidget({
  campaigns,
  maxCampaigns = 5,
  showViewAll = true,
  isLoading = false,
  emptyMessage = 'No campaigns yet',
  className = '',
}: CampaignPerformanceWidgetProps) {
  const displayedCampaigns = campaigns.slice(0, maxCampaigns);

  if (isLoading) {
    return (
      <div
        className={`bg-white rounded-xl border border-gray-200/60 p-6 shadow-sm ${className}`}
        data-testid="campaign-performance-loading"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <span className="w-1 h-6 bg-gradient-to-b from-primary-500 to-primary-700 rounded-full"></span>
          Top Campaigns
        </h2>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-4 border border-gray-200 rounded-xl animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-1/2 mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
              <div className="h-2 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div
        className={`bg-white rounded-xl border border-gray-200/60 p-6 shadow-sm ${className}`}
        data-testid="campaign-performance-empty"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <span className="w-1 h-6 bg-gradient-to-b from-primary-500 to-primary-700 rounded-full"></span>
          Top Campaigns
        </h2>
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-gray-500 text-sm mb-4">{emptyMessage}</p>
          <Link to="/campaigns" className="btn btn-primary btn-sm">
            Create Campaign
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-xl border border-gray-200/60 p-6 shadow-sm hover:shadow-md transition-shadow duration-300 ${className}`}
      data-testid="campaign-performance-widget"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <span className="w-1 h-6 bg-gradient-to-b from-primary-500 to-primary-700 rounded-full"></span>
          Top Campaigns
        </h2>
        {showViewAll && (
          <Link
            to="/campaigns"
            className="text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1"
          >
            View all
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        )}
      </div>

      <div className="space-y-4">
        {displayedCampaigns.map((campaign, index) => {
          const performance = getPerformanceLevel(campaign.bot_percentage);

          return (
            <div
              key={campaign.id}
              className="group relative p-4 border-2 border-gray-200 rounded-xl hover:border-primary-300 hover:shadow-md transition-all duration-300"
              data-testid="campaign-card"
            >
              {/* Rank Badge */}
              <div className="absolute -top-3 -left-3 w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                {index + 1}
              </div>

              {/* Campaign Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <Link
                    to={`/campaigns/${campaign.id}`}
                    className="text-base font-bold text-gray-900 hover:text-primary-600 transition-colors block mb-1"
                  >
                    {campaign.name}
                  </Link>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      <span className="font-semibold">{campaign.requests_count.toLocaleString()}</span>
                      <span className="text-gray-500">requests</span>
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                {campaign.status && (
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                      campaign.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {campaign.status === 'active' ? 'Active' : 'Paused'}
                  </span>
                )}
              </div>

              {/* Bot Detection Bar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-gray-700">Bot Detection</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${performance.textColor}`}>
                      {campaign.bot_percentage.toFixed(1)}%
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                      campaign.bot_percentage < 30
                        ? 'bg-green-100 text-green-700'
                        : campaign.bot_percentage < 50
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {performance.label}
                    </span>
                  </div>
                </div>
                <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`absolute left-0 top-0 h-full ${performance.color} rounded-full transition-all duration-500`}
                    style={{ width: `${campaign.bot_percentage}%` }}
                  ></div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
                <Link
                  to={`/statistics?campaignId=${campaign.id}`}
                  className="flex-1 py-2 px-3 bg-primary-50 hover:bg-primary-100 text-primary-700 font-semibold text-xs rounded-lg transition-colors text-center"
                >
                  View Stats
                </Link>
                <Link
                  to={`/campaigns/${campaign.id}`}
                  className="flex-1 py-2 px-3 bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold text-xs rounded-lg transition-colors text-center"
                >
                  Manage
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CampaignPerformanceWidget;
